import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

import {DSelect, HDatePicker, HInput, HTextarea, HCheckbox, HRadio} from '../../../../validation/HForm';
import LoaderInbox from '../Loader/LoaderInbox';
import { calculationAccess, getDocumentName } from '../../../../helpers/common';
import { automaticOperationReasons, listRoles } from '../../../../helpers/data-constants';
import { createTransactionScheme, transactionRecalculationScheme } from '../../../../validation/scheme/transactionScheme';
import { getEmployee, getReasons, resetHandbook } from '../../../../redux/actions/handbookAction';
import { getLastTransaction } from '../../../../redux/actions/transactionsAction';
import { Message } from '../../../utilites';
import './modalsInBoxOperation.scss';
import CrossIcon from '../../../../assets/icons/cross.svg';

const rolesForAccessByDate = [
  listRoles.ADMIN,
  listRoles.DIRECTOR,
  listRoles.BRANCH_DIRECTOR,
  listRoles.ACCOUNTANT,
];

const rolesForLimitedAccessByDate = [
  listRoles.GOODS_SPECIALIST
];

const ModalInboxOperationContent = ({
  cashBoxName,
  type,
  closeModelCreateTransaction,
  onTransactionSubmit,
  disabledButton,
  refundThings = false,
  recalculation = false,
  transactionData,
  messageInfoElement
}) => {
  const { users, employeesError, usersLoading, reasons, reasonsLoading, reasonsError } =
    useSelector(({ handbook }) => handbook);
  const { lastTransaction, lastTransactionLoading, commonError } = useSelector(
    ({ transactions }) => transactions,
  );
  const { client } = useSelector(({ clientInfo }) => clientInfo);

  const [transactionDoc, setTransactionDoc] = useState(1);
  const [previousTransactionDoc, setPreviousTransactionDoc] = useState(0);
  const [isCancel, setIsCancel] = useState(false);
  const [employees, setEmployees] = useState([{ value: 0, label: null }]);
  const [disabled, setDisabled] = useState(false);
  const [options, setOptions] = useState({
    defaultSum: '',
    defaultReason: null,
    defaultAccepted: null,
    defaultDescription: '',
    defaultDisabled: false,
    defaultDocument: '',
  });

  const getTransactionScheme = () => {
    return recalculation ? transactionRecalculationScheme() : createTransactionScheme(type);
  };

  const form = useForm({
    mode: 'onBlur',
    resolver: joiResolver(getTransactionScheme()),
  });

  const dispatch = useDispatch();

  const accessByDate = calculationAccess(rolesForAccessByDate);
  const limitedAccessByDate = calculationAccess(rolesForLimitedAccessByDate);
  const composeError = reasonsError || commonError || employeesError;

  useEffect(() => {
    if (users.length) {
      setEmployees((prev) => [prev[0], ...users.map((u) => ({ value: u.id, label: u.fullName }))]);
    } else {
      setEmployees((prev) => [prev[0]]);
    }
  }, [users]);

  useEffect(() => {
    if (type) {
      dispatch(getReasons({ value: '', type }));
      dispatch(getLastTransaction(type));
    }
  }, [type, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetHandbook());
    };
  }, [dispatch]);

  useEffect(() => {
    if (lastTransaction && lastTransaction?.documentNumber) {
      setTransactionDoc(parseInt(lastTransaction.documentNumber) + 1);
      setPreviousTransactionDoc(lastTransaction.documentNumber);
    }
  }, [lastTransaction]);

  function handleReasonChange(val, action) {
    if (action.action === 'input-change') {
      getReasons({ value: val, type });
    }
  }

  let nextDocumentNumber =
    lastTransaction && lastTransaction.documentNumber
      ? parseInt(lastTransaction.documentNumber) + 1
      : 1;

  const returnElementDateFromRole = () => {
    const datePickerProps = {
      h: "true",
      className: "input",
      defaultValue: moment().utc().format('YYYY-MM-DD'),
      name: "date",
      id: "date",
    };

    if(recalculation) {
      datePickerProps.defaultValue = transactionData.closureDate
      datePickerProps.min = moment(transactionData.closureDate).format('YYYY-MM-DD')
      datePickerProps.max = moment(transactionData.closureDate).format('YYYY-MM-DD')
    }

    if (accessByDate) {
      return <HDatePicker {...datePickerProps}/>;
    }
    else if (limitedAccessByDate) {
      return (
        <HDatePicker
          {...datePickerProps}
          min={moment().utc().add(-1, 'days').format('YYYY-MM-DD')}
          max={moment().utc().format('YYYY-MM-DD')}
        />
      );
    } else {
      return (
        <HDatePicker
          {...datePickerProps}
          min={moment().utc().format('YYYY-MM-DD')}
          max={moment().utc().format('YYYY-MM-DD')}
        />
      );
    }
  };

  const handleGetEmployee = (e, action) => {
    if (action.action === 'input-change' && e.length >= 2) {
      dispatch(getEmployee({ bySearch: e, document: true, active: 'active=true' }));
    }

    setEmployees((prev) => [{ value: 0, label: e }, ...prev.slice(1)]);
  };

  const handleChoose = (i) => {
    if (type !== 'CREDIT') {
      const passport = users.find((u) => u.id === i.value)?.document;
      form.setValue('passport', passport || '');
    }
  };

  const handleChange = () => {
    const value = form.getValues();
    if (value.toPerson) {
      if (type === 'CREDIT') {
        form.setValue(
          'accepted',
          { value: 0, label: 'физических лиц' },
          { shouldValidate: true },
        );
        setDisabled(true);
      } else {
        form.setValue(
          'accepted',
          { value: 0, label: 'физическим лицам' },
          { shouldValidate: true },
        );
        setDisabled(true);
      }
    } else {
      form.setValue('accepted', [{ value: 0, label: null }]);
      setDisabled(false);
    }
  };

  const handleCancelTransaction = (e) => {
      e.preventDefault();
      setIsCancel(true)
      closeModelCreateTransaction();
  };

  const calculateOptions = useCallback(() => {
    const newOptions = {};

    if (refundThings) {
        newOptions.defaultSum = transactionData.cash;
        newOptions.defaultReason = automaticOperationReasons.REFUND;
        newOptions.defaultDescription = `Номер ${transactionData.dealNumber}`;
    } else if (recalculation) {
        newOptions.defaultSum = transactionData?.recalculation.toFixed(2);
        newOptions.defaultReason = automaticOperationReasons.RECULCULATION;
        newOptions.defaultDescription = 'Ведомость перерасчёта № б/н от';
        newOptions.defaultAccepted = {
            value: 0,
            label: `${client.lastName} ${client.firstName} ${client.middleName}`,
        } || null;
        newOptions.defaultDocument = getDocumentName(client);
        newOptions.defaultDisabled = true;
    }

    setOptions(newOptions);
    if (recalculation) {
        setDisabled(true);
    }
  }, [refundThings, recalculation, transactionData, reasons, client]);

  useEffect(() => {
      calculateOptions();
      const updateValue = isCancel ? -1 : 1;

      if (recalculation) {
          form.setValue('amount', transactionData?.recalculation.toFixed(2));
          form.setValue('documentNumber', parseInt(transactionDoc) + updateValue);
          setTransactionDoc(prev => parseInt(prev) + updateValue);
          setPreviousTransactionDoc(prev => parseInt(prev) + updateValue);
      }
      setIsCancel(false)

  }, [transactionData?.recalculation, calculateOptions]);

  const documentNumberChange = (e) => {
    setTransactionDoc(e.target.value);
  };

  let firstReasonsOptions = [];
  let reasonsOptions = [...reasons];
  const creditOptions = ['Для ссудных операций из ломбарда', 'Для ссудных операций из банка'];
  const debitOptions = ['Для ссудных операций в ломбард', 'Безнал. расчет за ломбардные операции', 'Сдача в банк'];

  for (let i = 0; i < (type === 'CREDIT' ? creditOptions.length : debitOptions.length); i++) {
    let itemIndex = reasonsOptions.findIndex(item => item.label.toLowerCase() === (type === 'CREDIT'
    ? creditOptions[i].toLowerCase()
    : debitOptions[i].toLowerCase()))
    if(itemIndex !== -1) {
      firstReasonsOptions.push(reasonsOptions[itemIndex])
      reasonsOptions.splice(itemIndex, 1)
    }
  }
  reasonsOptions = [...firstReasonsOptions, ...reasonsOptions]

  const styleOperationData = type === 'DEBIT' ? 'modal-expenditure-transaction' : 'modal-credit-transaction'

  const { defaultSum, defaultReason, defaultAccepted, defaultDescription, defaultDisabled, defaultDocument } = options;

  return (
    <>
      {composeError && <Message type="error" text={composeError} />}
      {messageInfoElement}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onTransactionSubmit)}>
          <div className="modal-header">
            {!lastTransactionLoading && <><h2>
              {type === 'CREDIT' ? 'Приходная' : 'Расходная'} операция №
              {transactionDoc ? transactionDoc : 1}
            </h2><button
              className="button-icontrigger modal-closetrigger"
              onClick={recalculation ? handleCancelTransaction : closeModelCreateTransaction}>
                <span className="icon">
                  <CrossIcon />
                </span>
              </button></>}
          </div>
          <div className="modal-body">
            {reasonsLoading || lastTransactionLoading ? (
              <LoaderInbox />
            ) : (
              <div>
                <fieldset>
                  <div className="modal-subdivision">
                    <span className="label">{cashBoxName}</span>
                  </div>
                  <div className="d-flex flex-start align-baseline">
                    {lastTransaction?.documentNumber && (
                      <span className={styleOperationData}>
                        <big>
                          {`Предыдущий №${previousTransactionDoc} от
                          ${moment(lastTransaction.createDate).utc().format('DD.MM.YYYY')}${
                          lastTransaction.user ? ', ' + lastTransaction.user.fullName : ''}`}
                        </big>
                      </span>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="#">
                          Основание
                        </label>
                        <div className="form-element-data combobox-lg">
                          <DSelect
                              h="true"
                              name={`section`}
                              isMulti={false}
                              options={refundThings ? automaticOperationReasons.REFUND : reasonsOptions}
                              isSearchable={true}
                              handleOptions={handleReasonChange}
                              isLoading={false}
                              placeholder="Введите основание чтобы отфильтровать"
                              defaultValue={defaultReason}
                              isDisabled={recalculation}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="#">
                          Дата
                        </label>
                        <div className="form-element-data date">{returnElementDateFromRole()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="#">
                          Форма расчёта
                        </label>
                        <HRadio
                          h="true"
                          labels={['Наличный', 'Безналичный']}
                          name="payment"
                          checkboxStyle={true}
                          defaultCheckedLabel={'Наличный'}
                          disabled={refundThings || recalculation}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="">
                          Сумма, BYN
                        </label>
                        <div className="form-element-data number">
                          <HInput
                            h="true"
                            name="amount"
                            className="input"
                            type="text"
                            decimals={2}
                            defaultValue={defaultSum}
                            readOnly={refundThings}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="">
                          Номер документа
                        </label>
                        <div className="form-element-data">
                          <HInput
                            h="true"
                            className="input"
                            type="number"
                            name="documentNumber"
                            id="documentNumber"
                            onChange={documentNumberChange}
                            defaultValue={nextDocumentNumber}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="toPerson">
                          {type === 'CREDIT' ? 'От физических лиц' : 'Физическим лицам'}
                        </label>
                        <div className="form-element-data">
                          <HCheckbox
                            h="true"
                            name="toPerson"
                            className="input"
                            id="toPerson"
                            onChange={handleChange}
                            defaultValue={recalculation}
                            disabled={defaultDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="#">
                          {type === 'CREDIT' ? 'Принято от' : 'Выдать'}
                        </label>
                        <div className="form-element-data combobox-lg">
                          <DSelect
                            h="true"
                            name="accepted"
                            defaultValue={defaultAccepted}
                            options={employees}
                            handleOptions={handleGetEmployee}
                            handleOnChange={handleChoose}
                            isLoading={usersLoading}
                            placeholder="Введите фамилию"
                            isDisabled={disabled}
                            isClearable={disabled}
                            className='inBoxOperation-accepted'
                          />
                        </div>
                      </div>
                    </div>
                    {type === 'DEBIT' && (
                      <div className="form-row-item">
                        <div className="form-element">
                          <label className="form-element-label" htmlFor="#">
                            Паспортные данные
                          </label>
                          <div className="form-element-data">
                            <HInput
                              h="true"
                              className="input"
                              type="text"
                              name="passport"
                              id="passport"
                              defaultValue={defaultDocument}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-element">
                    <label className="form-element-label" htmlFor="#">
                      Приложение
                    </label>
                    <div className="form-element-data textarea">
                      <HTextarea
                        h="true"
                        className="input"
                        rows="4"
                        cols="10"
                        name="description"
                        id="description"
                        defaultValue={defaultDescription}
                      />
                    </div>
                  </div>
                  <div className="form-element">
                    <label className="form-element-label" htmlFor="#">
                      Дополнительная информация
                    </label>
                    <div className="form-element-data textarea">
                      <HTextarea
                        h="true"
                        className="input"
                        rows="4"
                        cols="10"
                        name="attachment"
                        id="attachment"
                      />
                    </div>
                  </div>
                </fieldset>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="button" disabled={disabledButton}>Сохранить</button>
            <button className="button-light" onClick={recalculation ? handleCancelTransaction : closeModelCreateTransaction}>
              Отменить
            </button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default ModalInboxOperationContent;
